import { type ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import type { RecordObject, RecordRef } from "@/profitability/records/objects";
import { sameRecord } from "@/profitability/records/objects";
import type { RecordList } from "@/profitability/records/record-panel";
import {
  RecordValue,
  type RecordValueSpec,
} from "@/profitability/records/record-value";
import { reportError } from "@/profitability/utils";
import {
  findingName,
  findingRef,
  findingSentence,
  findingShortfall,
  recordKeyLabel,
} from "./finding-row";
import { ReviewFilters } from "./review-filters";
import { ReviewHeading } from "./review-heading";
import { byRule } from "./review-list";
import { ReviewRuleFilter } from "./review-rule-filter";
import { useReviewSpec } from "./review-spec";
import { SeverityMark } from "./severity-mark";
import type { Finding, FindingFilter } from "./types";
import { useReviewQueue } from "./use-review-queue";

const QUEUE_EMPTY: Record<FindingFilter, { title: string; detail: string }> = {
  open: {
    title: "Nothing waiting for review",
    detail: "Findings from the next run show up here.",
  },
  closed: {
    title: "No closed findings",
    detail: "Findings you close show up here.",
  },
  all: {
    title: "No findings yet",
    detail: "This agent has not flagged anything yet.",
  },
};

/** Finding rows, in the shape the queue paints them. */
export const ReviewQueueSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <Stack gap={2} aria-hidden>
    {Array.from({ length: Math.max(rows, 1) }, (_, row) => (
      <Skeleton
        key={row}
        className={cn({
          "h-10 w-2/3": rows === 0,
          "h-14 w-full rounded-lg": rows !== 0,
        })}
      />
    ))}
  </Stack>
);

/** The column's order, so previous and next walk the rows as they read. */
export const reviewQueueList = (
  findings: Finding[],
  order: string[] = [],
): RecordList => ({
  label: "Review",
  refs: byRule(findings, order).flatMap((group) => group.items.map(findingRef)),
});

/**
 * The queue as a column: a rule's name, then each finding as its badge, one
 * line of what was found, and the amount. Status and the record stay off
 * the row.
 */
export const ReviewColumn = ({
  objects,
  queue,
  selected,
  reviewing = false,
  heading,
  actions,
  onOpen,
  onScopeChange,
}: {
  objects: RecordObject[];
  queue: ReturnType<typeof useReviewQueue>;
  selected: RecordRef | null;
  reviewing?: boolean;
  /** The period, which stays with the list rather than on the finding. */
  heading?: ReactNode;
  actions?: ReactNode;
  onOpen: (ref: RecordRef, list: RecordList) => void;
  onScopeChange: (filter: FindingFilter, rules: string[]) => void;
}) => {
  const spec = useReviewSpec();
  const {
    findings,
    items,
    filter,
    setFilter,
    selectedRules,
    setRules,
    countOf,
    countsReady,
  } = queue;
  const loading =
    findings.isPending || (findings.isPlaceholderData && items.length === 0);
  const changeRules = (rules: string[]) => {
    onScopeChange(filter, rules);
    setRules(rules);
  };
  const groups = byRule(items, spec.rule_order);
  const totals = new Map(
    queue.ruleCounts.map((item) => [item.rule, item.count]),
  );
  const list = reviewQueueList(items, spec.rule_order);
  const amountOf = (finding: Finding): RecordValueSpec | null => {
    const below = findingShortfall(spec, finding);
    const loss = finding.margin !== null && finding.margin < 0;
    if (below !== null)
      return { kind: "money", amount: below, short: true, loss };
    if (finding.margin === null) return null;
    return {
      kind: "rate",
      value: finding.margin,
      loss,
      label: spec.metric.label,
    };
  };

  return (
    <Stack
      gap={0}
      className="min-h-0 flex-1 pointer-coarse:[&_[data-slot=button]]:min-h-11 pointer-coarse:[&_[data-slot=button]]:min-w-11 pointer-coarse:[&_button]:min-h-11 pointer-coarse:[&_button]:min-w-11"
    >
      <ReviewHeading actions={actions}>{heading}</ReviewHeading>
      <Stack
        direction="row"
        align="center"
        justify="between"
        gap={2}
        className="w-full shrink-0 px-4 pt-1 pb-3"
      >
        <ReviewFilters
          value={filter}
          onChange={(value) => {
            onScopeChange(value, selectedRules);
            setFilter(value);
          }}
          countOf={countOf}
          countsReady={countsReady}
        />
        <ReviewRuleFilter
          rules={queue.ruleCounts}
          selected={selectedRules}
          onChange={changeRules}
          pending={findings.isPending}
        />
      </Stack>
      <Stack className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-3">
        {loading ? (
          <ReviewQueueSkeleton />
        ) : findings.isError ? (
          <Stack gap={2} className="px-2">
            <Text role="alert" tone="destructive">
              {reportError(findings.error)}
            </Text>
            <Button
              variant="secondary"
              className="self-start"
              onClick={() => void findings.refetch()}
            >
              Try again
            </Button>
          </Stack>
        ) : items.length === 0 && selectedRules.length > 0 ? (
          <Stack gap={2} className="px-2 py-4">
            <Text variant="title">Nothing in this rule</Text>
            <Text tone="muted">
              Other findings may still be in this period.
            </Text>
            <Button
              variant="secondary"
              className="self-start"
              onClick={() => changeRules([])}
            >
              All rules
            </Button>
          </Stack>
        ) : items.length === 0 && reviewing && filter === "open" ? (
          <Text role="status" tone="muted" className="px-2 py-4">
            Reviewing…
          </Text>
        ) : items.length === 0 ? (
          <Stack gap={1} className="px-2 py-4">
            <Text variant="title">{QUEUE_EMPTY[filter].title}</Text>
            <Text tone="muted">{QUEUE_EMPTY[filter].detail}</Text>
          </Stack>
        ) : (
          groups.map((group, index) => (
            <section
              key={group.rule}
              aria-label={group.rule}
              className={cn({ "pt-4": index > 0 })}
            >
              <Stack
                direction="row"
                align="baseline"
                justify="between"
                gap={2}
                className="px-2 pt-1 pb-1.5"
              >
                <Stack direction="row" align="baseline" gap={1.5}>
                  <Text variant="caption" tone="muted" weight="medium">
                    {group.rule}
                  </Text>
                  <Text variant="caption" tabular className="text-subtlest">
                    {totals.get(group.rule)}
                  </Text>
                </Stack>
              </Stack>
              <ul className="flex list-none flex-col gap-1 p-0">
                {group.items.map((finding) => {
                  const object = objects.find(
                    (item) => item.dataset.id === finding.dataset,
                  );
                  const ref = findingRef(finding);
                  const current = sameRecord(selected, ref);
                  const name =
                    findingName(finding, object) || (object?.name ?? "Record");
                  const amount = amountOf(finding);
                  return (
                    <li key={finding.id}>
                      <button
                        type="button"
                        aria-current={current || undefined}
                        onClick={() => onOpen(ref, list)}
                        className={cn(
                          "flex min-h-11 w-full min-w-0 items-start gap-x-3 rounded-lg px-2 py-2.5 text-start transition-[background-color] duration-150 ease-out outline-none focus-visible:ring-3 focus-visible:ring-focus/50 focus-visible:ring-inset active:bg-neutral-subtle-pressed",
                          {
                            "bg-neutral": current,
                            "hover:bg-neutral-subtle-hovered": !current,
                          },
                        )}
                      >
                        <span className="mt-0.5">
                          <SeverityMark
                            severity={finding.severity}
                            hypothesis={finding.hypothesis}
                          />
                        </span>
                        <span className="min-w-0 flex-1">
                          <RecordValue
                            spec={{
                              kind: "badge",
                              label: recordKeyLabel(finding, object) ?? name,
                            }}
                          />
                          <span className="mt-1 block truncate text-sm text-subtle">
                            {findingSentence(finding)}
                          </span>
                        </span>
                        {amount && (
                          <RecordValue
                            spec={amount}
                            className="shrink-0 whitespace-nowrap"
                          />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))
        )}
        {findings.hasNextPage && (
          <Button
            variant="ghost"
            className="self-center"
            loading={findings.isFetchingNextPage}
            loadingLabel="Loading more findings"
            onClick={() => void findings.fetchNextPage()}
          >
            Show more
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
